import FiscalYearTableCell from "components/company/financials/american/FiscalYearTableCell";
import formatFinancialValue from "components/company/financials/formatFinancialValue";
import FinancialValueThresholdColumnStyles from "components/financials/FinancialValueThresholdColumnStyles";

import TextTooltip from "components/shared/TextTooltip";
import type { IAmericanCompanyIssuerFinancialsFieldsFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import { palette } from "lib/miolo";

interface IReitFinancialsProps {
  onFilterByQuarter: (reportDate: Date) => void;
  displayAllColumns: boolean;
  financials:
    | IAmericanCompanyIssuerFinancialsFieldsFragment["yearly"]
    | IAmericanCompanyIssuerFinancialsFieldsFragment["quarterly"];
}

export default function ReitFinancials({ onFilterByQuarter, displayAllColumns, financials }: IReitFinancialsProps) {
  if (financials.length === 0) {
    return null;
  }

  const isQuarterly = financials.every((row) => row.statementType === "QUARTERLY");

  const hasPayout = isQuarterly
    ? false
    : financials.some((financialStatementRow) => {
        return financialStatementRow.ratios.dividendPayoutRatio > 0;
      });
  const hasRepurchase = false;

  const hasDividendYield = financials.some((financialStatementRow) => {
    return financialStatementRow.ratios.dividendYield > 0;
  });

  const hasNetIncomeFromDiscontinuedOperations = financials.some((financialStatementRow) => {
    return financialStatementRow.incomeStatement.netIncomeDiscontinuedOperations > 0;
  });

  return (
    <table
      css={{
        borderRadius: 4,
        borderSpacing: 0,
        borderCollapse: "collapse",
        "> thead > tr > th:first-of-type": {
          borderTopLeftRadius: 4,
        },
        "> thead > tr > th:last-of-type": {
          borderTopRightRadius: 4,
        },
        "> tbody > tr:last-of-type > td:first-of-type": {
          borderBottomLeftRadius: 4,
        },
        "> tbody > tr:last-of-type > td:last-of-type": {
          borderBottomRightRadius: 4,
        },
        "> thead > tr > th": {
          position: "sticky",
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
        "& > tbody > tr > td": {
          padding: "6px 12px",
          fontSize: 13.3,
          lineHeight: 1.33,
        },
      }}
    >
      <thead>
        <tr>
          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.white,
              fontWeight: 600,
              backgroundColor: palette.blueBayoux,
              borderRight: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            Year
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.lynch}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/totalEquity" placement="top" content="Assets - Liabilities">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Equity
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/revenue" placement="top" content="Income from Products and Services">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Revenue
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/ebitda"
              placement="top"
              content="Earnings Before Interest, Taxes, Depreciation and Amortization"
              style={{
                maxWidth: 260,
                textAlign: "center",
              }}
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  EBITDA
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              D&A
            </th>
          )}

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/ebit"
                placement="top"
                content="Earnings Before Interest and Taxes"
                style={{
                  maxWidth: 260,
                  textAlign: "center",
                }}
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    EBIT
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netInterestIncome" placement="top" content="Financial Result">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Fin. Res.
                </span>
              )}
            </TextTooltip>
          </th>

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              Taxes
            </th>
          )}

          {hasNetIncomeFromDiscontinuedOperations && displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/netIncomeFromDiscontinuedOperations"
                placement="top"
                content="Net Income from Discontinued Operations"
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Disc. Op.
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netIncome" placement="top" content="Revenue - Discounts">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Net Inc.
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.seaGreen,
              fontWeight: 600,
              backgroundColor: palette.grannyApple,
              borderLeft: `solid 1px ${palette.fringyFlower}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/netMargin" placement="top" content="Net Income / Revenue">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Net Mar.
                </span>
              )}
            </TextTooltip>
          </th>

          {!isQuarterly && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.seaGreen,
                fontWeight: 600,
                backgroundColor: palette.grannyApple,
                borderLeft: `solid 1px ${palette.fringyFlower}`,
                letterSpacing: 0.6,
              }}
            >
              ROE
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/cash" placement="top" content="Capital with Liquidity">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Cash
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.mojo,
              fontWeight: 600,
              backgroundColor: "#faecec",
              borderLeft: `solid 1px #edd1d1`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/debt" placement="top" content="Short-term Debt + Long-term Debt">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Debt
                </span>
              )}
            </TextTooltip>
          </th>

          {!isQuarterly && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mojo,
                fontWeight: 600,
                backgroundColor: "#faecec",
                borderLeft: `solid 1px #edd1d1`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/netDebtToEbitdaRatio" placement="top" content="Net Debt / EBITDA">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    N.D. / EBITDA
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/fundsFromOperations" placement="top" content="Funds from operations">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  FFO
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip
              id="tooltips/th/capex"
              placement="top"
              content="Capital Expenditure + Net Inv. Properties Purchase And Sale"
            >
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  CAPEX PPS
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/cashFlowFromFinancing" placement="top" content="Cash Flow from Financing">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  FCF
                </span>
              )}
            </TextTooltip>
          </th>

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.scienceBlue,
              fontWeight: 600,
              backgroundColor: palette.lilyWhite,
              borderLeft: `solid 1px ${palette.frenchPass}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/freeCashFlow" placement="top" content="Free Cash Flow">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Free
                </span>
              )}
            </TextTooltip>
          </th>

          {hasRepurchase && displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                fontWeight: 600,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/repurchaseOfStock" placement="top" content="Repurchase Of Capital Stock">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Buyback
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          <th
            className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
            css={{
              color: palette.luxorGold,
              fontWeight: 600,
              backgroundColor: palette.linen,
              borderLeft: `solid 1px ${palette.chamois}`,
              letterSpacing: 0.6,
            }}
          >
            <TextTooltip id="tooltips/th/cashFlowForDividends" placement="top" content="Cash Flow for Dividends">
              {({ accessibility, bind, ref }) => (
                <span {...accessibility} {...bind} ref={ref}>
                  Div.
                </span>
              )}
            </TextTooltip>
          </th>

          {hasPayout && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                fontWeight: 600,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip
                id="tooltips/th/cashFlowForDividendsToFundsFromOperationsRatio"
                placement="top"
                content="Cash Flow for Dividends / FFO"
              >
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    Payout
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {hasDividendYield && displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.luxorGold,
                fontWeight: 600,
                backgroundColor: palette.linen,
                borderLeft: `solid 1px ${palette.chamois}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/dividendYield" placement="top" content="Dividend Yield">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    DY
                  </span>
                )}
              </TextTooltip>
            </th>
          )}

          {displayAllColumns && (
            <th
              className="top-navbar-mobile-offset lg:top-navbar-desktop-offset"
              css={{
                color: palette.mineShaft,
                fontWeight: 600,
                backgroundColor: palette.catskillWhite,
                borderLeft: `solid 1px ${palette.mystic}`,
                letterSpacing: 0.6,
              }}
            >
              <TextTooltip id="tooltips/th/ffoPerShare" placement="top" content="FFO Per Share">
                {({ accessibility, bind, ref }) => (
                  <span {...accessibility} {...bind} ref={ref}>
                    FPS
                  </span>
                )}
              </TextTooltip>
            </th>
          )}
        </tr>
      </thead>

      <tbody
        css={{
          "& > tr > td": {
            textAlign: "center",
          },
          "& > tr > td:not(:first-of-type)": {
            borderLeft: `solid 1px ${palette.mystic}`,
            borderRight: `solid 1px ${palette.mystic}`,
            borderBottom: `solid 1px ${palette.mystic}`,
          },
          "& > tr:hover": {
            backgroundColor: "rgb(241, 244, 248, 0.5)",
          },
          "& > tr:last-of-type > td": {
            borderBottom: "none",
          },
        }}
      >
        {financials.map((financialStatementRow) => {
          return (
            <tr key={financialStatementRow.fiscalYear}>
              <FiscalYearTableCell
                row={financialStatementRow}
                onFilterByQuarter={(reportDate) => onFilterByQuarter(reportDate)}
              />

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.balanceSheet.totalEquity < 0 ? palette.mojo : palette.punga,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.balanceSheet.totalEquity)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.fruitSalad,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.revenue)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: financialStatementRow.incomeStatement.ebitda < 0 ? palette.mojo : palette.seaGreen,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.ebitda)}
              </td>

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: palette.mojo,
                    lineHeight: "20px",
                    textAlign: "right",
                    border: `solid 1px ${palette.fringyFlower}`,
                  }}
                >
                  {formatFinancialValue(financialStatementRow.incomeStatement.depreciationAndAmortization * -1)}
                </td>
              )}

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: financialStatementRow.incomeStatement.ebit < 0 ? palette.mojo : palette.seaGreen,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.fringyFlower}`,
                  }}
                >
                  {formatFinancialValue(financialStatementRow.incomeStatement.ebit)}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: financialStatementRow.incomeStatement.netInterestIncome >= 0 ? palette.seaGreen : palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.netInterestIncome)}
              </td>

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: financialStatementRow.incomeStatement.taxProvision >= 0 ? palette.seaGreen : palette.mojo,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.fringyFlower}`,
                    borderRight: "none",
                  }}
                >
                  {formatFinancialValue(financialStatementRow.incomeStatement.taxProvision)}
                </td>
              )}

              {hasNetIncomeFromDiscontinuedOperations && displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 600,
                    color:
                      financialStatementRow.incomeStatement.netIncomeDiscontinuedOperations >= 0
                        ? palette.oceanGreen
                        : palette.mojo,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.fringyFlower}`,
                    borderRight: "none",
                  }}
                >
                  {formatFinancialValue(financialStatementRow.incomeStatement.netIncomeDiscontinuedOperations)}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: financialStatementRow.incomeStatement.netIncome >= 0 ? palette.oceanGreen : palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.fringyFlower}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.incomeStatement.netIncome)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  color: financialStatementRow.incomeStatement.netIncome < 0 ? palette.mojo : palette.blueBayoux,
                  fontWeight: financialStatementRow.incomeStatement.netIncome < 0 ? 600 : 500,
                  lineHeight: "20px",
                  borderLeft: `solid 1px ${palette.mystic}`,
                  border: `solid 1px ${palette.mystic}`,
                  backgroundColor: "#fafafa",
                }}
              >
                {financialStatementRow.incomeStatement.netIncome < 0
                  ? "L"
                  : `${financialStatementRow.incomeStatement.netMargin
                      .toFixed(
                        financialStatementRow.incomeStatement.netMargin < 1 &&
                          financialStatementRow.incomeStatement.netMargin !== 0
                          ? 1
                          : 0,
                      )
                      .replace(".", ",")}%`}
              </td>

              {!isQuarterly && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight:
                      financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                      financialStatementRow.incomeStatement.netIncome < 0 ||
                      financialStatementRow.balanceSheet.totalEquity < 0
                        ? 600
                        : 500,
                    color:
                      financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                      financialStatementRow.incomeStatement.netIncome < 0 ||
                      financialStatementRow.balanceSheet.totalEquity < 0
                        ? palette.mojo
                        : palette.blueBayoux,
                    lineHeight: "20px",
                    borderLeft: `solid 1px ${palette.mystic}`,
                    border: `solid 1px ${palette.mystic}`,
                    borderRight: "none",
                    backgroundColor: "#fafafa",
                  }}
                >
                  {financialStatementRow.balanceSheet.returnOnEquity < 0 ||
                  financialStatementRow.incomeStatement.netIncome < 0 ||
                  financialStatementRow.balanceSheet.totalEquity < 0
                    ? "L"
                    : `${financialStatementRow.balanceSheet.returnOnEquity
                        .toFixed(financialStatementRow.balanceSheet.returnOnEquity < 1 ? 1 : 0)
                        .replace(".", ",")}%`}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.dixie,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.balanceSheet.cash)}
              </td>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 600,
                  color: palette.mojo,
                  lineHeight: "20px",
                  border: `solid 1px #eec9c9`,
                }}
              >
                {formatFinancialValue(financialStatementRow.balanceSheet.debt)}
              </td>

              {!isQuarterly && (
                <FinancialValueThresholdColumnStyles
                  value={financialStatementRow.balanceSheet.netDebtToEbitdaRatio}
                  comparators={[
                    (value) => {
                      return value >= 0 && value < 4;
                    },
                    (value) => {
                      return value >= 4 && value < 6;
                    },
                    (value) => {
                      return value >= 6;
                    },
                  ]}
                  styles={[
                    () => ({
                      color: palette.seaGreen,
                      backgroundColor: "#F1FFF3",
                    }),
                    () => ({
                      color: palette.kumera,
                      backgroundColor: palette.butteryWhite,
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.mojo,
                      backgroundColor: "#FCF1F1",
                    }),
                  ]}
                >
                  {(netDebtToEbitdaRatio, css) => {
                    return financialStatementRow.incomeStatement.ebitda < 0 ? (
                      <td
                        css={{
                          color: palette.mojo,
                          fontWeight: 600,
                          fontFamily: "novel-mono-pro-condensed",
                          lineHeight: "20px",
                          backgroundColor: "#FCF1F1",
                          border: `solid 1px #eec9c9`,
                        }}
                      >
                        L
                      </td>
                    ) : (
                      <td
                        css={{
                          color: palette.mojo,
                          fontWeight: 500,
                          fontFamily: "novel-mono-pro-condensed",
                          lineHeight: "20px",
                          border: `solid 1px #eec9c9`,
                          ...css,
                        }}
                      >
                        {netDebtToEbitdaRatio !== null && netDebtToEbitdaRatio % 1 === 0
                          ? `${netDebtToEbitdaRatio},0`
                          : String(netDebtToEbitdaRatio).replace(".", ",")}
                      </td>
                    );
                  }}
                </FinancialValueThresholdColumnStyles>
              )}

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.cashFlow.fundsFromOperations}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.tundora,
                  }),
                  () => ({
                    color: palette.stiletto,
                  }),
                ]}
              >
                {(fundsFromOperations, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      lineHeight: "20px",
                      border: `solid 1px ${palette.frenchPass}`,
                      ...css,
                    }}
                  >
                    {formatFinancialValue(fundsFromOperations)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: palette.tundora,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.frenchPass}`,
                }}
              >
                {formatFinancialValue(financialStatementRow.cashFlow.capitalExpenditure)}
              </td>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.cashFlow.cashFlowFromFinancing}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.stiletto,
                  }),
                  () => ({
                    color: palette.tundora,
                  }),
                ]}
              >
                {(cashFlowFromFinancing, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 500,
                      lineHeight: "20px",
                      border: `solid 1px ${palette.frenchPass}`,
                      ...css,
                    }}
                  >
                    {formatFinancialValue(cashFlowFromFinancing)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              <FinancialValueThresholdColumnStyles
                value={financialStatementRow.cashFlow.freeCashFlow}
                comparators={[(value) => value > 0, (value) => value < 0]}
                styles={[
                  () => ({
                    color: palette.seaGreen,
                    backgroundColor: "#F1FFF3",
                  }),
                  () => ({
                    color: palette.stiletto,
                    backgroundColor: "#FCF1F1",
                  }),
                ]}
              >
                {(freeCashFlow, css) => (
                  <td
                    css={{
                      fontFamily: "novel-mono-pro-condensed",
                      fontWeight: 600,
                      lineHeight: "20px",
                      border: `solid 1px ${palette.frenchPass}`,
                      borderRight: "none",
                      ...css,
                    }}
                  >
                    {formatFinancialValue(freeCashFlow)}
                  </td>
                )}
              </FinancialValueThresholdColumnStyles>

              {hasRepurchase && displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 500,
                    color: palette.luxorGold,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.chamois}`,
                    borderRight: "none",
                  }}
                >
                  {formatFinancialValue(financialStatementRow.cashFlow.repurchaseOfStock)}
                </td>
              )}

              <td
                css={{
                  fontFamily: "novel-mono-pro-condensed",
                  fontWeight: 500,
                  color: palette.luxorGold,
                  lineHeight: "20px",
                  border: `solid 1px ${palette.chamois}`,
                  borderRight: "none",
                }}
              >
                {formatFinancialValue(financialStatementRow.cashFlow.cashFlowForDividends)}
              </td>

              {hasPayout && (
                <FinancialValueThresholdColumnStyles
                  value={Math.round(financialStatementRow.ratios.dividendPayoutRatio * 100)}
                  comparators={[
                    (value) => {
                      return value > 0 && value < 30;
                    },
                    (value) => {
                      return value >= 30 && value < 70;
                    },
                    (value) => {
                      return value >= 70;
                    },
                  ]}
                  styles={[
                    () => ({
                      color: palette.mojo,
                      backgroundColor: "#FCF1F1",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.kumera,
                      backgroundColor: palette.butteryWhite,
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                    () => ({
                      color: palette.seaGreen,
                      backgroundColor: "#F1FFF3",
                      borderBottom: `solid 1px ${palette.chamois}`,
                    }),
                  ]}
                >
                  {(dividendPayoutRatio, css) => (
                    <td
                      css={{
                        fontFamily: "novel-mono-pro-condensed",
                        fontWeight: 500,
                        color: palette.luxorGold,
                        lineHeight: "20px",
                        border: `solid 1px ${palette.chamois}`,
                        borderRight: "none",
                        ...css,
                      }}
                    >
                      {dividendPayoutRatio}%
                    </td>
                  )}
                </FinancialValueThresholdColumnStyles>
              )}

              {hasDividendYield && displayAllColumns && (
                <td
                  css={{
                    color: palette.luxorGold,
                    fontWeight: 500,
                    fontFamily: "novel-mono-pro-condensed",
                    border: `solid 1px ${palette.chamois}`,
                    borderRight: "none",
                  }}
                >
                  {`${financialStatementRow.ratios.dividendYield
                    .toFixed(financialStatementRow.ratios.dividendYield === 0 ? 0 : 1)
                    .replace(".", ",")}%`}
                </td>
              )}

              {displayAllColumns && (
                <td
                  css={{
                    fontFamily: "novel-mono-pro-condensed",
                    fontWeight: 600,
                    color: financialStatementRow.incomeStatement.ffoPerShare < 0 ? palette.mojo : palette.blueBayoux,
                    lineHeight: "20px",
                    border: `solid 1px ${palette.mystic}`,
                  }}
                >
                  {`${
                    financialStatementRow.incomeStatement.ffoPerShare < 0
                      ? "L"
                      : financialStatementRow.incomeStatement.ffoPerShare
                          .toFixed(financialStatementRow.incomeStatement.ffoPerShare !== 0 ? 2 : 0)
                          .replace(".", ",")
                  }`}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
